// ====================================================================
// Knowledge Platform API Client
//
// Thin wrapper over invokeWithAuth('knowledge-query', ...).
// All calls go through the edge function — no direct table access.
//
// Auth:
//   - help-for-route: any valid Lina session
//   - search / entity / related / ai-context / status: admin session only
// ====================================================================

import { invokeWithAuth } from '@/lib/api/client'
import type {
  KnowledgeEntity,
  KnowledgeRelationship,
  KnowledgeSnapshot,
  DatabaseHealthOverview,
  KnowledgeSearchParams,
  KnowledgeRelatedParams,
  EntityType,
  LensScoringInput,
  LensScoringResult,
} from '@/types/knowledge'

// ---------------------------------------------------------------------------
// Route path → entity id helper
// ---------------------------------------------------------------------------

/**
 * Convert a bare route path (e.g. "/analyze") to a knowledge entity id.
 * Accepts fully-qualified ids too (pass-through).
 */
export function routePathToEntityId(pathOrId: string): string {
  if (pathOrId.startsWith('route:')) return pathOrId
  // Normalise: ensure leading slash, strip trailing slash except root
  const normalised = pathOrId.startsWith('/') ? pathOrId : `/${pathOrId}`
  return `route:app:${normalised}`
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

/**
 * help-for-route
 * Returns active support/help/faq entities linked to the given route.
 * Auth: any valid Lina session.
 */
export async function knowledgeHelpForRoute(pathOrId: string): Promise<KnowledgeEntity[]> {
  const id = routePathToEntityId(pathOrId)
  const { data, error } = await invokeWithAuth<KnowledgeEntity[]>('knowledge-query', {
    method: 'POST',
    body: { action: 'help-for-route', id },
  })
  if (error) throw new Error(`knowledge help-for-route failed: ${error.message}`)
  return data ?? []
}

/**
 * search
 * ILIKE search on entity name + id with optional type filter.
 * Pass `type` alone (no q) to browse all entities of that type.
 * Pass neither to list active entities (capped by limit).
 * Auth: admin only.
 */
export async function knowledgeSearch(params: KnowledgeSearchParams): Promise<KnowledgeEntity[]> {
  const body: Record<string, string | number> = { action: 'search' }
  if (params.q?.trim()) body.q = params.q.trim()
  if (params.type) body.type = params.type
  if (params.limit) body.limit = params.limit

  const { data, error } = await invokeWithAuth<KnowledgeEntity[]>('knowledge-query', {
    method: 'POST',
    body,
  })
  if (error) throw new Error(`knowledge search failed: ${error.message}`)
  return Array.isArray(data) ? data : []
}

/**
 * browseByType
 * List entities of a type. Uses the same search path that already works:
 * match the deterministic id prefix (`feature:`, `edge-function:`, …).
 * Auth: admin only.
 */
export async function knowledgeBrowseByType(
  type: EntityType,
  limit = 200,
): Promise<KnowledgeEntity[]> {
  // Prefer type-only browse (no q) so we get the full type set.
  // knowledge-query allows up to 500; Observatory needs >100 for weights.
  return knowledgeSearch({ type, limit })
}

/**
 * browseAllEntities
 * List active entities across all types (capped by limit).
 * Auth: admin only.
 */
export async function knowledgeBrowseAllEntities(limit = 100): Promise<KnowledgeEntity[]> {
  return knowledgeSearch({ limit })
}

/**
 * listRelationships
 * Page of relationships (newest first).
 * Auth: admin only.
 */
export async function knowledgeListRelationships(limit = 100): Promise<KnowledgeRelationship[]> {
  const { data, error } = await invokeWithAuth<KnowledgeRelationship[]>('knowledge-query', {
    method: 'POST',
    body: { action: 'list-relationships', limit },
  })
  if (error) throw new Error(`knowledge list-relationships failed: ${error.message}`)
  return Array.isArray(data) ? data : []
}

/**
 * entity
 * Fetch a single entity by id.
 * Auth: admin only.
 */
export async function knowledgeEntity(id: string): Promise<KnowledgeEntity> {
  const { data, error } = await invokeWithAuth<KnowledgeEntity>('knowledge-query', {
    method: 'POST',
    body: { action: 'entity', id },
  })
  if (error) throw new Error(`knowledge entity failed: ${error.message}`)
  if (!data) throw new Error(`Entity not found: ${id}`)
  return data
}

/**
 * related
 * Fetch relationships from/to an entity.
 * Auth: admin only.
 */
export async function knowledgeRelated(
  params: KnowledgeRelatedParams,
): Promise<KnowledgeRelationship[]> {
  const body: Record<string, string | number> = { action: 'related', id: params.id }
  if (params.relType) body.relType = params.relType
  if (params.direction) body.direction = params.direction
  if (params.limit) body.limit = params.limit

  const { data, error } = await invokeWithAuth<KnowledgeRelationship[]>('knowledge-query', {
    method: 'POST',
    body,
  })
  if (error) throw new Error(`knowledge related failed: ${error.message}`)
  return data ?? []
}

/**
 * ai-context
 * Download the latest AI context JSON from Storage.
 * Auth: admin only.
 */
export async function knowledgeAiContext(): Promise<unknown> {
  const { data, error } = await invokeWithAuth<unknown>('knowledge-query', {
    method: 'POST',
    body: { action: 'ai-context' },
  })
  if (error) throw new Error(`knowledge ai-context failed: ${error.message}`)
  return data
}

/**
 * status
 * Get the latest sync snapshot (last sync time, entity/relationship counts, git SHAs).
 * Auth: admin only.
 */
export async function knowledgeStatus(): Promise<KnowledgeSnapshot> {
  const { data, error } = await invokeWithAuth<KnowledgeSnapshot>('knowledge-query', {
    method: 'POST',
    body: { action: 'status' },
  })
  if (error) throw new Error(`knowledge status failed: ${error.message}`)
  if (!data) throw new Error('No knowledge snapshot found')
  return data
}

/**
 * Database health overview. This is deliberately read-only: it aggregates
 * extracted schema plus audit evidence and never mutates database objects.
 */
export async function knowledgeDatabaseOverview(): Promise<DatabaseHealthOverview> {
  const { data, error } = await invokeWithAuth<DatabaseHealthOverview>('knowledge-query', {
    method: 'POST',
    body: { action: 'database-overview' },
  })
  if (error) throw new Error(`knowledge database overview failed: ${error.message}`)
  if (!data) throw new Error('No database overview found')
  return data
}

// ---------------------------------------------------------------------------
// Re-export types for convenience
// ---------------------------------------------------------------------------

export type {
  KnowledgeEntity,
  KnowledgeRelationship,
  KnowledgeSnapshot,
  DatabaseHealthOverview,
  EntityType,
  LensScoringInput,
  LensScoringResult,
}
